import React, {useEffect, useState} from 'react';
import {Formik} from 'formik';
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {Box} from "@material-ui/core";

import AppButton from "../../../../components/CustomButtons/AppButton";
import SettingFields from "./SettingFields";
import {fetchAdminUserDetail, getAdminUserDetail, reduceUpdateAdminUser} from "../../../../store/slices/admin";
import {ADMIN_PERMISSIONS, SYSTEM_ROLE} from "../../../../config/AppConfig";
import { getAuthInfo } from 'store/slices/auth';
import { fetchCompanyUserDetail } from 'store/slices/companyUser';
import { getCompanyUserDetail } from 'store/slices/companyUser';
import { reduceUpdateCompanyUser } from 'store/slices/companyUser';

const hasAssignablePermission = (user, permissionKey) => {
    if (!Array.isArray(user.permissions)) {
        return true;
    }
    return ADMIN_PERMISSIONS[permissionKey] && user.permissions.includes(ADMIN_PERMISSIONS[permissionKey].id);
}

const AdminUserSettingForm = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector(getAuthInfo);
    const [submitting, setSubmitting] = useState(false);
    const initValues = {
        isEmailOnboard: false,
        isEmailCreatedParticipant: false,
        onboard: false,
        dashboard: false,
        createParticipant: false,
        users: false,
        jobs: false,
        company: false,
        workers: false,
        clients: false,
        booking: false,
        rates: false,
        billing: false,
        payroll: false,
        options: false,
        subscriptionPlan: false,
        transactions: false,
        accountSoftware: false,
        paymentGateway: false
    };
    const [initializeData, setInitializeData] = useState({
        isEmailOnboard: false,
        isEmailCreatedParticipant: false,
        onboard: false,
        dashboard: false,
        createParticipant: false,
        users: false,
        jobs: false,
        company: false,
        workers: false,
        clients: false,
        booking: false,
        rates: false,
        billing: false,
        payroll: false,
        options: false,
        subscriptionPlan: false,
        transactions: false,
        accountSoftware: false,
        paymentGateway: false

    });
    const adminUserDetail = useSelector(getAdminUserDetail);
    const companyUserDetail = useSelector(getCompanyUserDetail);

    const _submit = (data) => {
        setSubmitting(true);
        // setInitializeData({});
        const permissions = [];
        Object.keys(data).forEach(key => {
            if (data[key] === true && ADMIN_PERMISSIONS[key] && hasAssignablePermission(user, key)) {
                permissions.push(ADMIN_PERMISSIONS[key].id);
            }
        });
        if (user.role === SYSTEM_ROLE.COMPANY) { 
            dispatch(reduceUpdateCompanyUser({
                id: id,
                companyId: user.companyId,
                permissions,
                isEmailOnboard: data.isEmailOnboard,
                isEmailCreatedParticipant: data.isEmailCreatedParticipant
            })).then(({payload}) => {
                setSubmitting(false);
                if (payload) {
                    _loadData();
                }
            })
        } else {
            dispatch(reduceUpdateAdminUser({
                id,
                permissions,
                isEmailOnboard: data.isEmailOnboard,
                isEmailCreatedParticipant: data.isEmailCreatedParticipant
            })).then(({payload}) => {
                setSubmitting(false);
                if (payload) {
                    _loadData();
                }
            })
        }
    }
    const _loadData = () => {
        if(user.role === SYSTEM_ROLE.COMPANY) {
            dispatch(fetchCompanyUserDetail({ companyId: user.companyId, id: id }))
        } else {
            dispatch(fetchAdminUserDetail({id}));
        }
    }
    useEffect(() => {
        _loadData();
    }, [])
    useEffect(() => {
        if (adminUserDetail) {
            const data = {};
            if (adminUserDetail.adminPermissions) {
                adminUserDetail.adminPermissions.forEach(permissionAccess => {
                    switch (permissionAccess) {
                        case ADMIN_PERMISSIONS.dashboard.id:
                            data.dashboard = true;
                            break;
                        case ADMIN_PERMISSIONS.users.id:
                            data.users = true;
                            break;
                        case ADMIN_PERMISSIONS.company.id:
                            data.company = true;
                            break;
                        case ADMIN_PERMISSIONS.subscriptionPlan.id:
                            data.subscriptionPlan = true;
                            break;
                        case ADMIN_PERMISSIONS.transactions.id:
                            data.transactions = true;
                            break;
                        case ADMIN_PERMISSIONS.accountSoftware.id:
                            data.accountSoftware = true;
                            break;
                        case ADMIN_PERMISSIONS.paymentGateway.id:
                            data.paymentGateway = true;
                            break;
                        case ADMIN_PERMISSIONS.options.id:
                            data.options = true;
                            break;
                    }
                });
            }
            setInitializeData({
                ...initValues,
                ...data,
                isEmailOnboard: adminUserDetail.emailOnboard,
                isEmailCreatedParticipant: adminUserDetail.emailCreatedParticipant,
            });
        }
    }, [adminUserDetail]);
    useEffect(() => {
        if (companyUserDetail) {
            const data = {};
            if (companyUserDetail.permissions) {
                companyUserDetail.permissions.forEach(permissionAccess => {
                    switch (permissionAccess) {
                        case ADMIN_PERMISSIONS.dashboard.id:
                            data.dashboard = true;
                            break;
                        // case ADMIN_PERMISSIONS.users.id:
                        //     data.users = true;
                        //     break;
                        case ADMIN_PERMISSIONS.jobs.id:
                            data.jobs = true;
                            break;
                        case ADMIN_PERMISSIONS.workers.id:
                            data.workers = true;
                            break;
                        case ADMIN_PERMISSIONS.clients.id:
                            data.clients = true;
                            break;
                        case ADMIN_PERMISSIONS.rates.id:
                            data.rates = true;
                            break;
                        case ADMIN_PERMISSIONS.booking.id:
                            data.booking = true;
                            break;
                        case ADMIN_PERMISSIONS.billing.id:
                            data.billing = true;
                            break;
                        case ADMIN_PERMISSIONS.payroll.id:
                            data.payroll = true;
                            break;
                        case ADMIN_PERMISSIONS.options.id:
                            data.options = true;
                            break;
                    }
                });
            }
            setInitializeData({
                ...initValues,
                ...data,
                isEmailOnboard: companyUserDetail.emailOnboard,
                isEmailCreatedParticipant: companyUserDetail.emailCreatedParticipant,
            });
        }
    }, [companyUserDetail]);

    return (
        <Formik
            initialValues={initializeData}
            onSubmit={_submit}
            enableReinitialize
        >
            {({handleSubmit}) => (
                <>
                    <SettingFields role={user.role} permissions={user.permissions}/>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3}>
                        <AppButton
                            label={t('button.save')}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            disabled={submitting}
                        />
                    </Box>
                </>
            )}
        </Formik>
    )
}

export default AdminUserSettingForm;
